// The composition root. One owner for the session, one owner for the route, and
// the refusals that happen before either.
//
// THE ORDER BELOW IS THE DESIGN.
//
//   1. A build with no server is refused, before a login form is drawn. A form
//      that collects an address and a code and then cannot send them anywhere is
//      worse than a refusal, because the person has been told to check their mail.
//
//   2. The session is read from storage. Absent, unparseable or expired all read
//      as absent, and the store clears the last two rather than leaving them.
//
//   3. No session means the login screen, whatever the url said. An activation
//      link opened cold lands here, which is correct and is also the one place a
//      nicety is missing — see the note on `wanted` below.
//
// ONE PLACE CLEARS THE SESSION AND IT IS THIS FILE. Every surface that discovers
// a dead session calls up rather than clearing it, so there is no second path
// that could clear the store and forget the state, or the state and forget the
// store. session/store.ts argues why a 401 clears and a 403 must not; this is
// where that rule is executed.
//
// SIGNING OUT ASKS THE SERVER FIRST AND SIGNS OUT REGARDLESS. The revoke is what
// makes the token dead rather than merely forgotten, and a failed revoke must not
// trap somebody in a session they have asked to leave. Both halves happen, in
// that order, and the local half is in a `finally` so that no failure of the
// first can skip it.

import { lazy, Suspense, useCallback, useMemo, useState, type ReactNode } from "react";
import { NoticeBox } from "@nova/desk-ui/parts";
import { Loading } from "@nova/desk-ui/states";
import { BASE_URL, SERVER_SENTENCE, THIS_BUILD } from "../build/thisBuild.js";
import { endSession, type ApiOptions } from "../api/client.js";
import type { ReportName } from "../api/endpoints.js";
import { clearKioskViewHrefs } from "../session/kioskView.js";
import {
  browserSessionStore,
  sessionOnStore,
  sessionToken,
  type Session,
  type SessionStore,
  type SessionToken,
} from "../session/store.js";
import { useRoute } from "../routes/useRoute.js";
import { Chrome } from "./Chrome.js";
import { DismissedScope } from "./Dismissed.js";
import { Choose } from "./Choose.js";
import { Clients } from "./Clients.js";
import { Account } from "./Account.js";
import { Activity } from "./Activity.js";
import { Enrol } from "./Enrol.js";
import { FirstFactor } from "./FirstFactor.js";
import { Team } from "./Team.js";
import { Notices } from "./Notices.js";
import { People } from "./People.js";
import { Dashboard } from "./Dashboard.js";
import { Export } from "./Export.js";
import { OptIn } from "./OptIn.js";
import { Reconciliation } from "./Reconciliation.js";
import { Survey } from "./Survey.js";
import { ShowReader } from "./Show.js";
import { ShowDetails } from "./ShowDetails.js";
import { SignIn } from "./SignIn.js";
import { Tablets } from "./Tablets.js";
import { Unverified } from "./Unverified.js";
import { Draw } from "./Draw.js";
import { Wheel } from "./Wheel.js";
import { WheelPrizes } from "./WheelPrizes.js";
import { WheelResults } from "./WheelResults.js";
import { Themes } from "./Themes.js";
import { ClientTheme } from "./ClientTheme.js";
import { SurfaceTheme } from "./SurfaceTheme.js";
import { BoardSetup } from "./BoardSetup.js";
import { NotForRole, pageTitle } from "./NotForRole.js";
import { opens, pageOf, type Reader } from "./roleAccess.js";

// THE THREE HEAVIEST SCREENS ARE FETCHED WHEN SOMEBODY OPENS ONE, not with the
// sign-in page. Kiosk, Registration and Ride and the rules under them are about
// half the screen code, and they are never where anybody lands: the directory
// and the dashboard are. So the first load is the frame, the directory and the
// show's reads, and these arrive on the first visit to their tab. Everything a
// screen decides is unchanged; only when its code is downloaded moves.
const Kiosk = lazy(() => import("./Kiosk.js").then((module) => ({ default: module.Kiosk })));
const Registration = lazy(() => import("./Registration.js").then((module) => ({ default: module.Registration })));
const Ride = lazy(() => import("./Ride.js").then((module) => ({ default: module.Ride })));

/** What stands in for a screen while its code arrives: the same skeleton a read draws. */
function Fetching({ children }: { readonly children: ReactNode }): ReactNode {
  return <Suspense fallback={<Loading rows={4} cols={3} label="Opening…" />}>{children}</Suspense>;
}

export type AppProps = {
  /**
   * How a request is made, and where the session is kept.
   *
   * Both injected with defaults rather than reached for, so check.mjs can drive
   * the whole application over a recorded transport and a store it can read
   * afterwards. That is the difference between asserting what this application
   * does and asserting what it calls.
   */
  readonly transport?: ApiOptions["transport"];
  readonly store?: SessionStore;
};

export function App({ transport, store }: AppProps): ReactNode {
  const sessions = useMemo(
    () => store ?? sessionOnStore(browserSessionStore()),
    [store],
  );

  const api = useMemo<ApiOptions>(
    () => ({ baseUrl: BASE_URL, transport: transport ?? ((url, init) => fetch(url, init)) }),
    [transport],
  );

  // Read once, on the first render, rather than in an effect. An effect would
  // draw the login screen for one frame to somebody who is signed in, and that
  // flash is indistinguishable from having been signed out.
  const [session, setSession] = useState<Session | null>(() => sessions.read());
  const [because, setBecause] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  /**
   * The address to offer back on the sign-in form, set only when somebody
   * signs out to sign straight back in: the authenticator gate's last step,
   * where the session in hand cannot be stamped with the factor just set up.
   */
  const [resumeEmail, setResumeEmail] = useState<string | null>(null);
  /**
   * Set where the browser would not keep the session.
   *
   * A different kind of message from `because`, which is why it is a different
   * piece of state: `because` says why you are looking at a login form, and this
   * says what is true about the session you have just been given. Somebody who
   * signs in on a locked-down profile and reloads in half an hour would otherwise
   * be sent back to their inbox with no idea why.
   */
  const [storageWarning, setStorageWarning] = useState<string | null>(null);
  /**
   * The handle every authenticated request reads its token from.
   *
   * KEYED ON `sessionId` AND NOT ON `session`, AND THAT IS THE ENTIRE POINT
   * RATHER THAN AN OPTIMISATION. A new handle on every render of a new `session`
   * object would be a new prop for nine surfaces, and every one of them has a
   * fetching effect keyed on it — so the console would refetch, which would
   * rotate, which would make a new session object, forever. Keyed on the id, the
   * handle is created once per sign-in and survives every rotation, which is
   * exactly the lifetime the token has.
   *
   * `sessions` is in the deps because a different store is a different place to
   * write replacements. It is stable for the life of the app in both the real
   * build and in check.mjs, so this costs nothing and says what it means.
   *
   * The initial value is read from `session` and then deliberately not tracked.
   * Any later change to the token comes through `adopt`, which is the one writer,
   * and a handle that also re-read React state would have two sources for one
   * value and no rule for which wins.
   */
  const token = useMemo(
    () => (session === null ? null : sessionToken(sessions, session)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above: rebuilding
    // this on every `session` object is the refetch loop the handle exists to avoid.
    [sessions, session?.sessionId],
  );

  const { route, navigate } = useRoute();

  const forget = useCallback(
    (detail: string | null): void => {
      sessions.clear();
      clearKioskViewHrefs(browserSessionStore());
      setSession(null);
      setBecause(detail);
      navigate({ kind: "sign_in" });
    },
    [sessions, navigate],
  );

  const onSessionEnded = useCallback((detail: string): void => forget(detail), [forget]);

  const signOutTo = useCallback((resume: string | null): void => {
    const held = token;
    if (held === null) return;

    setSigningOut(true);
    void (async () => {
      try {
        // Asked for, and the answer deliberately not branched on. The server
        // sends the same body whether or not the token was live, because telling
        // a caller otherwise tells somebody who found a token in a log file
        // whether it is worth trying.
        //
        // The handle rather than a token captured earlier, so that a rotation
        // that happened while somebody was reading a report does not leave this
        // sending a stale generation. api/src/auth/sessions.ts accepts a spent
        // token here on purpose and argues why, so this is belt and braces —
        // but the braces are the difference between signing out and thinking
        // you have.
        await endSession(api, held);
      } finally {
        setSigningOut(false);
        // No sentence. The person asked for this, so telling them why they are
        // looking at a login form would be explaining their own decision to them.
        forget(null);
        setResumeEmail(resume);
      }
    })();
  }, [api, token, forget]);

  const onSignOut = useCallback((): void => signOutTo(null), [signOutTo]);

  const onSignedIn = useCallback(
    (fresh: Session): void => {
      // Held in state whether or not the store took it. A browser that refuses
      // to store is a browser this console still works in for the life of the
      // page, and refusing to sign somebody in over it would be choosing the
      // strictest outcome for the person with the strictest browser.
      const stored = sessions.write(fresh);
      setSession(fresh);
      setBecause(null);
      setResumeEmail(null);
      setStorageWarning(
        stored
          ? null
          : "This browser cannot remember your sign-in. Reloading the page will sign you out.",
      );
      navigate({ kind: "choose" });
    },
    [sessions, navigate],
  );

  // ---------------------------------------------------------------- refusals

  if (!THIS_BUILD.ok) {
    // No login form, on purpose. There is nothing this build can do with an
    // address and a code, and offering to take them would be collecting a
    // credential in order to throw it away.
    return (
      <div className="console-root">
        {/* The sign-in gate's frame, the design's one page without a desk
            around it, since this build has no desk to offer. */}
        <main className="console-page gate">
          <div className="gate-box">
            <h1 className="gate-h">Console is not connected</h1>
            <NoticeBox kind="amber">
              {/* The build tool's own sentence, unaltered. One wording for the
                  terminal and the screen, so whoever reads this is reading what
                  whoever built it would have seen. */}
              <p>{THIS_BUILD.problem}</p>
            </NoticeBox>
            <p className="help help-tight">Sign-in is unavailable until this build is replaced.</p>
          </div>
        </main>
      </div>
    );
  }

  // `token === null` is the same condition as `session === null` — the memo above
  // derives one from the other — and both are written because TypeScript cannot
  // see that and because a reader should not have to either. It is not a second
  // case: there is no state in which somebody is signed in and has no token.
  if (session === null || token === null) {
    return (
      <SignIn
        api={api}
        onSignedIn={onSignedIn}
        // Stored at step 4, before "Open the console", by the same store every
        // sign-in writes to: a reload there opens the desk. Whether the browser
        // kept it is said by onSignedIn when the console opens.
        onKeep={(fresh) => {
          sessions.write(fresh);
        }}
        because={because}
        email={resumeEmail}
      />
    );
  }

  // NO AUTHENTICATOR, NO DESK (Gabe, 11 Oct; #202). Every console role needs a
  // proved second factor for every read, so a session that proved none sets
  // one up before anything else is drawn. `false` only: a stored session from
  // an older build carries `null`, which is not a statement that there is none.
  // FirstFactor.tsx carries the argument in full.
  if (session.secondFactorProved === false) {
    return (
      <FirstFactor
        api={api}
        token={token}
        session={session}
        onSessionEnded={onSessionEnded}
        signingOut={signingOut}
        onSignOut={onSignOut}
        onSignInAgain={() => signOutTo(session.email)}
      />
    );
  }

  // -------------------------------------------------------------- signed in

  const activationId =
    route.kind === "report" ||
    route.kind === "kiosk" ||
    route.kind === "tablets" ||
    route.kind === "registration" ||
    route.kind === "details" ||
    route.kind === "ride" ||
    route.kind === "people" ||
    route.kind === "unverified" ||
    route.kind === "draw" ||
    route.kind === "wheel" ||
    route.kind === "board" ||
    route.kind === "themes" ||
    route.kind === "activity"
      ? route.activationId
      : null;
  const here =
    route.kind === "report"
      ? route.report
      : route.kind === "client" || route.kind === "clients" || route.kind === "client_theme"
        ? "clients"
        : route.kind === "kiosk" ||
            route.kind === "tablets" ||
            route.kind === "account" ||
            route.kind === "enrol" ||
            route.kind === "notices" ||
            route.kind === "team" ||
            route.kind === "choose" ||
            route.kind === "registration" ||
            route.kind === "details" ||
            route.kind === "ride" ||
            route.kind === "people" ||
            route.kind === "unverified" ||
            route.kind === "draw" ||
            route.kind === "wheel" ||
            route.kind === "board" ||
            route.kind === "themes" ||
            route.kind === "activity"
          ? route.kind
          : "choose";

  // WHAT THIS ROUTE OPENS FOR THIS READER, FROM THE ONE MAP (roleAccess.ts).
  // A page the map closes is not drawn and asks the service nothing: a deep
  // link to it meets NotForRole under the rail, rather than the page's own
  // title and filters over a refusal. The map is tied to the service's guards
  // by check.mjs, which is what makes holding it here safe; a route the
  // service opens is never closed here, and the check fails if one is.
  const reader: Reader = { role: session.role, canExport: session.canExport };
  const page = pageOf(route);
  // Unverified emails draws the design's own refusal for a reader it is not
  // for (console-live.jsx, NotThisSignIn), asking the same map, so it is left
  // to draw it rather than given the generic one.
  const closed = page !== null && page !== "unverified" && !opens(reader, page);

  return (
    <ShowReader api={api} token={token} activationId={activationId} onSessionEnded={onSessionEnded}>
    <Chrome
      session={session}
      signingOut={signingOut}
      onSignOut={onSignOut}
      nav={{
        activationId,
        here,
        reader,
        onChoose: () => navigate({ kind: "choose" }),
        onClients: () => navigate({ kind: "clients" }),
        onOpen: (report) =>
          navigate({
            kind: "report",
            report,
            activationId: activationId ?? "",
          }),
        onRegistration: (id, panel = "open") =>
          navigate({ kind: "registration", activationId: id, panel }),
        registrationPanel: route.kind === "registration" ? route.panel : null,
        // The /tablets address is Kiosk › Devices under an older name, so the
        // rail opens Kiosk on Devices there too rather than on no page at all.
        kioskPanel: route.kind === "kiosk" ? route.panel : route.kind === "tablets" ? "devices" : null,
        wheelPanel: route.kind === "wheel" ? route.panel : null,
        boardPanel: route.kind === "board" ? route.panel : null,
        ridePanel: route.kind === "ride" ? route.panel : null,
        onKiosk: (id, panel = "schedule") => navigate({ kind: "kiosk", activationId: id, panel }),
        onDetails: (id) => navigate({ kind: "details", activationId: id }),
        onRide: (id, panel = "roster") =>
          navigate({ kind: "ride", activationId: id, riderId: null, panel }),
        // Always the list, never whichever person was open last. The rail is how
        // somebody leaves one record, and a rail item that reopened it would
        // have no way back to the list.
        onPeople: (id) => navigate({ kind: "people", activationId: id, attendeeRef: null }),
        onUnverified: (id) => navigate({ kind: "unverified", activationId: id }),
        onDraw: (id) => navigate({ kind: "draw", activationId: id }),
        onWheel: (id, panel = "setup") => navigate({ kind: "wheel", activationId: id, panel }),
        onBoard: (id, panel = "setup") => navigate({ kind: "board", activationId: id, panel }),
        onActivity: (id) => navigate({ kind: "activity", activationId: id }),
        // The library, never whichever theme was open last: the rail is how
        // somebody leaves one theme, as it is how they leave one person.
        onThemes: (id) => navigate({ kind: "themes", activationId: id, themeId: null }),
        client:
          route.kind === "client"
            ? { clientId: route.clientId, page: "details" }
            : route.kind === "client_theme"
              ? { clientId: route.clientId, page: "theme" }
              : null,
        onClient: (id, page) =>
          navigate(page === "details" ? { kind: "client", clientId: id } : { kind: "client_theme", clientId: id, themeId: null }),
        onEnrol: () => navigate({ kind: "enrol" }),
        onAccount: () => navigate({ kind: "account" }),
        onNotices: () => navigate({ kind: "notices" }),
        onTeam: () => navigate({ kind: "team" }),
      }}
    >
      <DismissedScope sessionId={session.sessionId}>
      {storageWarning === null ? null : (
        <NoticeBox kind="amber" title="This browser cannot remember your sign-in">
          <p>{storageWarning}</p>
        </NoticeBox>
      )}

      {closed && page !== null ? (
        <NotForRole
          title={pageTitle(page)}
          role={session.role}
          {...(activationId !== null && opens(reader, "dashboard")
            ? {
                leaveLabel: "This show's dashboard",
                onLeave: () => navigate({ kind: "report", report: "operator", activationId }),
              }
            : { leaveLabel: "Your activations", onLeave: () => navigate({ kind: "choose" }) })}
        />
      ) : (
      <>

      {route.kind === "report" ? (
        <Report
          report={route.report}
          api={api}
          token={token}
          activationId={route.activationId}
          onSessionEnded={onSessionEnded}
          role={session.role}
          onEnrol={() => navigate({ kind: "enrol" })}
          onReconciliation={() =>
            navigate({ kind: "report", report: "reconciliation", activationId: route.activationId })
          }
          onOptIn={() => navigate({ kind: "report", report: "optIn", activationId: route.activationId })}
          onSurvey={() => navigate({ kind: "report", report: "survey", activationId: route.activationId })}
        />
      ) : null}

      {/* ONE MOUNT FOR BOTH ADDRESSES. /tablets is Kiosk › Devices under an older
          name, and the rail takes it to /kiosk/devices; drawn in two places, the
          move remounted the page and lost a one-time code that cannot be read
          back. Found by Bugbot on #147. */}
      {(route.kind === "kiosk" && route.panel === "devices") || route.kind === "tablets" ? (
        <Tablets
          api={api}
          token={token}
          activationId={route.activationId}
          onSessionEnded={onSessionEnded}
          role={session.role}
          onEnrol={() => navigate({ kind: "enrol" })}
        />
      ) : null}

      {route.kind === "kiosk" && route.panel === "theme" ? (
        // The theme library's page for the hall screens. Its own mount rather
        // than a panel inside Kiosk.tsx, as Devices is: it reads nothing the
        // kiosk page reads, and the kiosk page's playlist reads are not wanted
        // to open it.
        <SurfaceTheme
          key={route.activationId}
          api={api}
          token={token}
          activationId={route.activationId}
          surface="kiosk"
          label="Kiosk"
          head={{ title: "Kiosk · theme", lede: "What the hall screens wear around the content." }}
          onOpenTheme={(themeId) => navigate({ kind: "themes", activationId: route.activationId, themeId })}
          onSessionEnded={onSessionEnded}
        />
      ) : null}

      {route.kind === "kiosk" && route.panel !== "devices" && route.panel !== "theme" ? (
        <Fetching>
        <Kiosk
          key={route.activationId}
          api={api}
          token={token}
          sessionId={session.sessionId}
          activationId={route.activationId}
          panel={route.panel}
          itemId={route.itemId ?? null}
          onItem={(itemId, how) =>
            navigate(
              itemId === null
                ? { kind: "kiosk", activationId: route.activationId, panel: "content" }
                : { kind: "kiosk", activationId: route.activationId, panel: "content", itemId },
              how,
            )
          }
          onSessionEnded={onSessionEnded}
          role={session.role}
          onEnrol={() => navigate({ kind: "enrol" })}
          onDetails={
            opens(reader, "details")
              ? () => navigate({ kind: "details", activationId: route.activationId })
              : undefined
          }
          actor={session.email ?? ""}
          onBoard={() => navigate({ kind: "board", activationId: route.activationId, panel: "setup" })}
        />
        </Fetching>
      ) : null}

      {route.kind === "ride" ? (
        // An Admin's and an Editor's (roleAccess.ts, `game`); Sponsor is an
        // Admin's alone. A route the map closes is NotForRole, above.
        <Fetching>
        <Ride
          api={api}
          token={token}
          activationId={route.activationId}
          riderId={route.riderId}
          panel={route.panel}
          onOpen={(id) =>
            navigate({ kind: "ride", activationId: route.activationId, riderId: id, panel: "roster" })
          }
          onList={() =>
            navigate({ kind: "ride", activationId: route.activationId, riderId: null, panel: "roster" })
          }
          // The design's "Open this person's record →" (audit item 9), to a
          // reader the People page opens for.
          onPerson={
            opens(reader, "people")
              ? (ref) => navigate({ kind: "people", activationId: route.activationId, attendeeRef: ref })
              : undefined
          }
          onSessionEnded={onSessionEnded}
          role={session.role}
          onEnrol={() => navigate({ kind: "enrol" })}
        />
        </Fetching>
      ) : null}

      {route.kind === "people" ? (
        // EVERY ROLE'S SINCE 11 OCT (roleAccess.ts, `people`). The service's
        // `attendeeRecordsRefusal` admits a viewer session that proved a factor
        // to the list and to one record, read only, for the shows they are
        // granted, and files a report_access row for each page as it does for
        // an Admin's.
        <People
          api={api}
          token={token}
          activationId={route.activationId}
          attendeeRef={route.attendeeRef}
          onOpen={(ref) =>
            navigate({ kind: "people", activationId: route.activationId, attendeeRef: ref })
          }
          onList={() =>
            navigate({ kind: "people", activationId: route.activationId, attendeeRef: null })
          }
          onSessionEnded={onSessionEnded}
          role={session.role}
          onEnrol={() => navigate({ kind: "enrol" })}
        />
      ) : null}

      {route.kind === "unverified" ? (
        // AN ADMIN'S ALONE (roleAccess.ts, `unverified`): the service refuses a
        // Viewer by attendeesAreNovaOnly and an Editor by requireService, which
        // takes the service's own position and nothing else. The screen asks
        // the same map before it mounts its read, so it is safe drawn anywhere.
        <Unverified
          api={api}
          token={token}
          activationId={route.activationId}
          onPeople={() =>
            navigate({ kind: "people", activationId: route.activationId, attendeeRef: null })
          }
          onSessionEnded={onSessionEnded}
          role={session.role}
          onEnrol={() => navigate({ kind: "enrol" })}
        />
      ) : null}

      {route.kind === "draw" ? (
        <Draw
          api={api}
          token={token}
          activationId={route.activationId}
          onOpenPerson={(ref) =>
            navigate({ kind: "people", activationId: route.activationId, attendeeRef: ref })
          }
          onSessionEnded={onSessionEnded}
          role={session.role}
          onEnrol={() => navigate({ kind: "enrol" })}
        />
      ) : null}

      {route.kind === "wheel" && route.panel === "setup" ? (
        <Wheel
          api={api}
          token={token}
          activationId={route.activationId}
          onSessionEnded={onSessionEnded}
          role={session.role}
          onEnrol={() => navigate({ kind: "enrol" })}
        />
      ) : null}

      {route.kind === "wheel" && route.panel === "prizes" ? (
        <WheelPrizes
          api={api}
          token={token}
          activationId={route.activationId}
          onSessionEnded={onSessionEnded}
          onSetup={() => navigate({ kind: "wheel", activationId: route.activationId, panel: "setup" })}
          role={session.role}
          onEnrol={() => navigate({ kind: "enrol" })}
        />
      ) : null}

      {route.kind === "wheel" && route.panel === "results" ? (
        <WheelResults
          api={api}
          token={token}
          activationId={route.activationId}
          onSessionEnded={onSessionEnded}
          reader={{ role: session.role, canExport: session.canExport }}
          onEnrol={() => navigate({ kind: "enrol" })}
        />
      ) : null}

      {route.kind === "board" && route.panel === "setup" ? (
        // The show's one board record (157, decision C11): every role reads
        // it, an Admin and a holding Editor save it (roleAccess.ts, `board`
        // and `boardEdit`).
        <BoardSetup
          key={route.activationId}
          api={api}
          token={token}
          activationId={route.activationId}
          reader={reader}
          onSessionEnded={onSessionEnded}
          onEnrol={() => navigate({ kind: "enrol" })}
          onStandings={
            opens(reader, "game")
              ? () => navigate({ kind: "ride", activationId: route.activationId, riderId: null, panel: "leaderboard" })
              : undefined
          }
          onScreens={
            opens(reader, "kioskScreens")
              ? () => navigate({ kind: "kiosk", activationId: route.activationId, panel: "screens" })
              : undefined
          }
        />
      ) : null}

      {route.kind === "board" && route.panel === "theme" ? (
        // What the leaderboard wears, on the shared SurfaceTheme. Publish and
        // the kiosk both read this pointer (157).
        <SurfaceTheme
          key={route.activationId}
          api={api}
          token={token}
          activationId={route.activationId}
          surface="board"
          label="Board"
          head={{
            title: "Board · theme",
            lede: "What the leaderboard wears. It shares the Wheel's token set, so one theme can dress both.",
          }}
          onOpenTheme={(themeId) => navigate({ kind: "themes", activationId: route.activationId, themeId })}
          onSessionEnded={onSessionEnded}
        />
      ) : null}

      {route.kind === "themes" ? (
        // AN ADMIN'S, AND AN EDITOR'S FOR THE CLIENTS THEY HOLD (roleAccess.ts,
        // `themes`, decision C1): the theme routes scope an Editor to their
        // clients and keep Nova's own themes read only. Keyed by the show and not by the
        // theme, so the library read is kept while somebody goes from a card to
        // a theme and back.
        <Themes
          key={route.activationId}
          api={api}
          token={token}
          activationId={route.activationId}
          themeId={route.themeId}
          onOpen={(themeId) => navigate({ kind: "themes", activationId: route.activationId, themeId })}
          onSessionEnded={onSessionEnded}
        />
      ) : null}

      {route.kind === "activity" ? (
        // EVERY ROLE'S (roleAccess.ts, `activity`). This endpoint answers a
        // client viewer a real page: migration 131 puts
        // the feed behind two views and withholds who at Nova acted by privilege
        // rather than by refusing the read, so a narrower page is the answer
        // instead of a 403. The rail advertises it to both roles for the same
        // reason, and Activity.tsx draws the withheld attribution as its own
        // thing rather than as a blank.
        //
        // `role` is still passed, because `Refusal` needs it to decide whether
        // an enrolment offer belongs under a failure, and an unstamped
        // nova_admin can meet a 403 here like anywhere else.
        <Activity
          api={api}
          token={token}
          activationId={route.activationId}
          onSessionEnded={onSessionEnded}
          role={session.role}
          onEnrol={() => navigate({ kind: "enrol" })}
        />
      ) : null}

      {route.kind === "details" ? (
        // On the rail under Dashboard, and still reached from the kiosk card.
        // An Admin's and an Editor's for the clients they hold (roleAccess.ts,
        // `details`): requireAmender refuses a viewer the write.
        <ShowDetails
          api={api}
          token={token}
          activationId={route.activationId}
          onSessionEnded={onSessionEnded}
          role={session.role}
          onEnrol={() => navigate({ kind: "enrol" })}
        />
      ) : null}

      {route.kind === "registration" ? (
        <Fetching>
        <Registration
          api={api}
          token={token}
          activationId={route.activationId}
          panel={route.panel}
          onPanel={(panel) => navigate({ kind: "registration", activationId: route.activationId, panel })}
          onSessionEnded={onSessionEnded}
          // An Admin's and an Editor's for the clients they hold (roleAccess.ts,
          // `registration`). The client record and Add person are an Admin's
          // (`clients`, `addContact`), so an Editor is shown neither.
          onOpenClient={opens(reader, "clients") ? (id) => navigate({ kind: "client", clientId: id }) : null}
          canAddContact={opens(reader, "addContact")}
          onOpenTheme={(themeId) => navigate({ kind: "themes", activationId: route.activationId, themeId })}
        />
        </Fetching>
      ) : null}

      {route.kind === "clients" || route.kind === "client" ? (
        // An Admin's (roleAccess.ts, `clients`): a client viewer has one
        // client, and a list of other companies is 010's leak wearing a
        // directory's clothes. The service refuses an Editor the list too.
        <Clients
          api={api}
          token={token}
          clientId={route.kind === "client" ? route.clientId : null}
          onOpen={(id) => navigate({ kind: "client", clientId: id })}
          onOpenActivation={(id) => navigate({ kind: "report", report: "operator", activationId: id })}
          onList={() => navigate({ kind: "clients" })}
          onSessionEnded={onSessionEnded}
        />
      ) : null}

      {route.kind === "client_theme" ? (
        // An Admin's, under Clients, for Clients' reason and the theme routes'
        // own (roleAccess.ts, `clientTheme`).
        <ClientTheme
          key={route.clientId}
          api={api}
          token={token}
          clientId={route.clientId}
          themeId={route.themeId}
          onOpenTheme={(themeId) => navigate({ kind: "client_theme", clientId: route.clientId, themeId })}
          onSessionEnded={onSessionEnded}
        />
      ) : null}

      {route.kind === "enrol" ? (
        // Open to anyone signed in (roleAccess.ts, `settings`). Enrolment is
        // offered by the server to any live session, and every role's own
        // factor and sessions are theirs. What is role-dependent is whether the
        // rail offers Settings, which waits on decision C2.
        <Enrol
          api={api}
          token={token}
          session={session}
          onSessionEnded={onSessionEnded}
          onSignOut={onSignOut}
        />
      ) : null}

      {/* Both open to anyone signed in, on the same argument the enrolment
          screen carries above: what is role-dependent is where a screen is
          advertised, not who may open it.

          The account screen DOES now read from the service — its sign-ins panel
          does, once somebody asks it to — so it takes the api and the token, and
          onSessionEnded for the one refusal that means this session is over.
          Notices still reads nothing. */}
      {route.kind === "account" ? (
        <Account session={session} api={api} token={token} onSessionEnded={onSessionEnded} />
      ) : null}
      {route.kind === "notices" ? <Notices /> : null}

      {route.kind === "team" ? (
        // An Admin's, in the rail's Account group (roleAccess.ts, `team`): the
        // service refuses the list to anybody else.
        <Team api={api} token={token} onSessionEnded={onSessionEnded} />
      ) : null}

      {route.kind === "choose" || route.kind === "sign_in" ? (
        // A signed-in person at /sign-in is shown the landing screen rather than
        // a login form. It is reachable by pressing back after signing in, and a
        // form that offers to sign in somebody who already is invites them to
        // burn a login code finding out that nothing happens.
        <Choose
          api={api}
          token={token}
          onOpen={(id) => navigate({ kind: "report", report: "operator", activationId: id })}
          onSessionEnded={onSessionEnded}
          role={session.role}
          onEnrol={() => navigate({ kind: "enrol" })}
        />
      ) : null}

      {route.kind === "not_found" ? (
        <div className="stack">
          <header className="head">
            <h1>Page not found</h1>
            <p className="lede">
              There is no Console page at <code>{route.path}</code>.
            </p>
          </header>
          <div className="acts">
            <button
              type="button"
              className="btn"
              onClick={() => navigate({ kind: "choose" })}
            >
              Your activations
            </button>
          </div>
          <p className="help help-tight">{SERVER_SENTENCE}</p>
        </div>
      ) : null}
      </>
      )}
      </DismissedScope>
    </Chrome>
    </ShowReader>
  );
}

type ReportProps = {
  readonly report: ReportName;
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string;
  readonly onSessionEnded: (detail: string) => void;
  readonly role: string;
  readonly onEnrol: () => void;
  readonly onReconciliation: () => void;
  readonly onOptIn: () => void;
  readonly onSurvey: () => void;
};

/**
 * One report's screen, chosen by name.
 *
 * AN EXHAUSTIVE SWITCH RATHER THAN A LOOKUP TABLE, because the exhaustiveness is
 * the point. `report` is a key of REPORT_ENDPOINTS, which is the service's own
 * constant — endpoints.ts says it is kept as one object precisely so the console
 * can prove it has a screen for each — so a fifth report added there stops this
 * function compiling until somebody decides what it looks like. A table with a
 * fallback would render nothing and pass every check.
 */
function Report({
  report,
  onReconciliation,
  onOptIn,
  onSurvey,
  ...props
}: ReportProps): ReactNode {
  switch (report) {
    case "operator":
      return (
        // Keyed by the show, so another show starts on the whole run with no
        // range, no last read and no rates carried over from this one.
        <Dashboard
          key={props.activationId}
          {...props}
          onReconciliation={onReconciliation}
          onOptIn={onOptIn}
          onSurvey={onSurvey}
        />
      );
    case "reconciliation":
      return <Reconciliation {...props} />;
    case "optIn":
      return <OptIn {...props} />;
    case "survey":
      return <Survey {...props} />;
    case "export":
      return <Export {...props} />;
  }
}
